import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { Box, Typography } from '@mui/material'
import { IconSparkles } from '@tabler/icons-react'
import { EditorContent, useEditor } from '@tiptap/react'
import Ai from '@tiptap-pro/extension-ai-advanced'

import Row from 'src/components/Row/Row'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { systemPromptForParentObject, type DocumentObject } from 'src/lib/pages'

import { extensionsForParentObject } from '../extensions'

const log = false

const logAiEditor = (...args) => {
  if (log) {
    logger.dev('AiEditor', ...args)
  }
}

const thinkingSx = {
  width: '100%',
  pl: '8px',
  pt: '24px',
  height: '42px',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: '4px',
  opacity: 0.7,
  '& .MuiTypography-root': {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '-0.4px',
  },
  '& .tabler-icon': {
    animation: 'growShrink 2s infinite',
  },
  '@keyframes growShrink': {
    '0%': {
      transform: 'scale(0.3)',
    },
    '50%': {
      transform: 'scale(1)',
    },
    '100%': {
      transform: 'scale(0.6)',
    },
  },
}

const forceProd = true

const streamUrl =
  process.env.HOST.includes('localhost') && !forceProd
    ? 'https://aa6acxvvavnjstyzypjhvjsxvi0uzcaz.lambda-url.us-east-1.on.aws/'
    : `https://f5gegaoxtd4kuyhc35q62wdyoq0ciwqh.lambda-url.us-east-1.on.aws/`

const AiEditorComponent = ({
  token,
  prompt,
  onSuccess,
  contextString,
  parentObject,
}: {
  token: string
  prompt: string
  onSuccess: (result: any) => void
  contextString: string
  parentObject: DocumentObject
}) => {
  const memoizedOnSuccess = useCallback(onSuccess, [onSuccess])
  const memoizedPrompt = useMemo(() => prompt, [prompt])
  const memoizedContextString = useMemo(() => contextString, [contextString])
  const memoizedToken = useMemo(() => token, [token])

  const aiInitialized = useRef(false)
  const successCalled = useRef(false)
  const contentPopulated = useRef(false)

  const { userCoreContact } = useContext(DayContext)

  const systemPrompt = useMemo(
    () => systemPromptForParentObject({ parentObject, userCoreContact }),
    [parentObject, userCoreContact]
  )

  const editor = useEditor({
    extensions: [
      ...extensionsForParentObject({
        parentObject,
      }),
      Ai.configure({
        appId: process.env.TIPTAP_APP_ID,
        token: memoizedToken,
        autocompletion: true,

        onChunk: (_) => {
          if (contentPopulated.current) {
            return
          }
          contentPopulated.current = true
        },

        onSuccess: (result) => {
          if (successCalled.current) {
            logger.dev('Success already called, skipping')
            return
          }
          successCalled.current = true
          memoizedOnSuccess(result)
        },
        aiStreamResolver: async ({ action, text, textOptions }) => {
          logAiEditor('aiStreamResolver called', { action, text, textOptions })
          const fetchOptions = {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              ...textOptions,
              text,
              modelId: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
            }),
          }

          const response = await fetch(streamUrl, fetchOptions)

          return response?.body
        },
      }),
    ],
    autofocus: false,
    editable: false,
  })

  useEffect(() => {
    const initializeAi = async () => {
      if (!editor || !memoizedPrompt) {
        logger.dev('Editor or prompt not found, skipping AI initialization')
        return
      }

      successCalled.current = false

      try {
        logAiEditor('Starting AI population', {
          token: memoizedToken,
          prompt: memoizedPrompt,
          contextString: memoizedContextString,
        })

        editor
          .chain()
          .aiTextPrompt({
            text: `${systemPrompt}\n\n<user-prompt>${memoizedPrompt}</user-prompt>`,
            stream: true,
            insert: true,
            format: 'rich-text',
            context: [
              {
                type: 'text',
                text: `${memoizedContextString}\n\n<user-prompt>${memoizedPrompt}</user-prompt>`,
              },
            ],
          })
          .run()
      } catch (error) {
        logger.error('AI initialization failed:', error)
      }
    }

    if (
      !aiInitialized.current &&
      editor &&
      memoizedPrompt &&
      memoizedToken &&
      memoizedContextString
    ) {
      aiInitialized.current = true
      initializeAi()
    }
  }, [
    editor,
    memoizedPrompt,
    memoizedContextString,
    memoizedToken,
    systemPrompt,
  ])

  useEffect(() => {
    aiInitialized.current = false
    successCalled.current = false
  }, [memoizedPrompt, memoizedToken])

  const containerSx = {
    width: '100%',
  }

  logAiEditor('AiEditorComponent', {
    token: memoizedToken,
    prompt: memoizedPrompt,
    contextString: memoizedContextString,
  })

  return (
    <Box sx={containerSx}>
      {!contentPopulated.current && (
        <Row sx={thinkingSx}>
          <IconSparkles size={16} />
          <Typography>Day.ai is thinking...</Typography>
        </Row>
      )}
      <div className="editor-tiptap-inner">
        <EditorContent editor={editor} />
      </div>
    </Box>
  )
}

const AiEditor = memo(AiEditorComponent, (previous, next) => {
  return previous.token === next.token && previous.prompt === next.prompt
})

AiEditor.displayName = 'AiEditor'

export default AiEditor
